/** Text-based attunement scene — legacy fallback for the graphical tree view, updated for V4. */
import Phaser from 'phaser';
import { ConfigLoader } from '../systems/ConfigLoader';
import type { GameScene } from './GameScene';
import type { V4NodeDefinition, V4ApotheosisDefinition } from '../utils/Types';
import { crispText, crispButton, setupCrispCamera } from '../ui/CrispUtil';
import { Font, Hex, TextStyle, ButtonColor, applyButtonHover } from '../ui/Theme';
import { GAME_WIDTH, GAME_HEIGHT } from '../utils/Constants';

const NODE_ROW_HEIGHT = 12;
const TAB_Y = 100;
const TREE_HEADER_Y = 126;
const NODE_START_Y = 144;
const HOTBAR_Y = 46;

/**
 * Renders all skill trees as a tabbed text view with V4 node types.
 * Slot assignment uses pending-click flow: click a node, then click an empty slot.
 */
export class AttunementSceneText extends Phaser.Scene {
  private gameSceneRef!: GameScene;

  // Managed display groups
  private slotGroup: Phaser.GameObjects.GameObject[] = [];
  private tabGroup: Phaser.GameObjects.GameObject[] = [];
  private nodeGroup: Phaser.GameObjects.GameObject[] = [];
  private infoGroup: Phaser.GameObjects.GameObject[] = [];
  private slotsRemainingText: Phaser.GameObjects.Text | null = null;

  private selectedTree = '';
  private selectedSkillId: string | null = null;
  private pendingAttuneId: string | null = null;

  constructor() {
    super({ key: 'AttunementSceneText' });
  }

  /** Initialises the scene, wires the ESC handler, and draws the first full view. */
  create(): void {
    this.gameSceneRef = this.scene.get('GameScene') as GameScene;
    setupCrispCamera(this);
    this.cameras.main.setBackgroundColor(Hex.sceneBg);

    const width = GAME_WIDTH;

    // Title
    crispText(this, width / 2, 14, 'ATTUNEMENTS', { fontFamily: Font.family, fontSize: Font.sizeButtonSm, color: Hex.debugDefault }).setOrigin(0.5);

    // Select first tree by default
    const treeNames = Object.keys(ConfigLoader.v4Trees);
    this.selectedTree = treeNames[0] ?? '';

    this.input.keyboard!.on('keydown-ESC', () => {
      if (this.pendingAttuneId) {
        this.pendingAttuneId = null;
        this.rebuildAll();
      } else {
        this.scene.start('PauseScene');
      }
    });

    this.rebuildAll();
  }

  // ---------------------------------------------------------------------------
  // Rebuild orchestration
  // ---------------------------------------------------------------------------

  /** Tears down and redraws every display group in sequence. */
  private rebuildAll(): void {
    this.rebuildSlots();
    this.rebuildTabs();
    this.rebuildNodes();
    this.rebuildInfo();
    this.rebuildSlotsRemaining();
  }

  // ---------------------------------------------------------------------------
  // Hotbar slots
  // ---------------------------------------------------------------------------

  /** Redraws the hotbar row, wiring assign/unassign click handlers based on pending state. */
  private rebuildSlots(): void {
    for (const obj of this.slotGroup) obj.destroy();
    this.slotGroup = [];

    const player = this.gameSceneRef.player;
    const width = GAME_WIDTH;
    const spacing = width / 5;

    // Section header
    const header = crispText(this, width / 2, HOTBAR_Y - 8, '— HOTBAR —', TextStyle.bodyMuted).setOrigin(0.5);
    this.slotGroup.push(header);

    const slotStyle = { fontFamily: Font.family, fontSize: Font.sizeTiny };
    const keyStyle = { fontFamily: Font.family, fontSize: Font.sizeLabel, color: Hex.textMuted } as Phaser.Types.GameObjects.Text.TextStyle;

    for (let i = 0; i < 4; i++) {
      const x = spacing * (i + 1);

      // Key label
      const keyText = crispText(this, x, HOTBAR_Y, `[${i + 1}]`, keyStyle).setOrigin(0.5);
      this.slotGroup.push(keyText);

      // Slot value
      const skillId = player.attunement.getSkillAtSlot(i);
      let label: string;
      let color: string;

      if (skillId) {
        const def = ConfigLoader.getV4Node(skillId);
        label = def?.name ?? skillId;
        color = Hex.cyan;
      } else if (this.pendingAttuneId) {
        label = '[assign]';
        color = '#ffdd00';
      } else {
        label = '[empty]';
        color = Hex.textDisabled;
      }

      const text = crispText(this, x, HOTBAR_Y + 12, label, { ...slotStyle, color }).setOrigin(0.5);

      if (this.pendingAttuneId && !skillId) {
        text.setInteractive({ useHandCursor: true });
        const slotIndex = i;
        const pending = this.pendingAttuneId;
        text.on('pointerdown', () => this.onSlotAssign(slotIndex, pending));
        text.on('pointerover', () => text.setColor('#ffffff'));
        text.on('pointerout', () => text.setColor('#ffdd00'));
      } else if (skillId) {
        text.setInteractive({ useHandCursor: true });
        const slotIndex = i;
        text.on('pointerdown', () => this.onSlotClick(slotIndex));
        text.on('pointerover', () => text.setColor('#ff6644'));
        text.on('pointerout', () => this.rebuildSlots());
      }

      this.slotGroup.push(text);
    }
  }

  // ---------------------------------------------------------------------------
  // Tree tabs
  // ---------------------------------------------------------------------------

  /** Redraws the tree-selection tab row, highlighting the currently active tree. */
  private rebuildTabs(): void {
    for (const obj of this.tabGroup) obj.destroy();
    this.tabGroup = [];

    const treeNames = Object.keys(ConfigLoader.v4Trees);
    const width = GAME_WIDTH;
    const tabSpacing = width / (treeNames.length + 1);

    for (let i = 0; i < treeNames.length; i++) {
      const name = treeNames[i];
      const x = tabSpacing * (i + 1);
      const isActive = name === this.selectedTree;

      const tab = crispText(this, x, TAB_Y, name.toUpperCase(), {
        fontFamily: Font.family,
        fontSize: Font.sizeLabel,
        color: isActive ? Hex.debugBright : Hex.textMuted,
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });

      if (!isActive) {
        applyButtonHover(tab, { default: Hex.textMuted, hover: Hex.textPrimary });
      }
      tab.on('pointerdown', () => {
        this.selectedTree = name;
        this.selectedSkillId = null;
        this.rebuildTabs();
        this.rebuildNodes();
        this.rebuildInfo();
      });

      this.tabGroup.push(tab);
    }
  }

  // ---------------------------------------------------------------------------
  // Tree nodes (single selected tree)
  // ---------------------------------------------------------------------------

  /** Redraws the node list for the selected tree, sorted by XP threshold across two columns. */
  private rebuildNodes(): void {
    for (const obj of this.nodeGroup) obj.destroy();
    this.nodeGroup = [];

    if (!this.selectedTree) return;

    const player = this.gameSceneRef.player;
    const width = GAME_WIDTH;

    // Tree XP header — left-aligned above the node list
    const treeXp = player.skillProgression.getTreeXp(this.selectedTree);
    const headerText = crispText(this, width / 2 - 100, TREE_HEADER_Y,
      `— ${this.selectedTree.toUpperCase()}  ·  XP: ${Math.floor(treeXp)} —`,
      TextStyle.bodyMuted,
    ).setOrigin(0.5);
    this.nodeGroup.push(headerText);

    // All nodes sorted by threshold — two columns on the left half
    const allNodes = ConfigLoader.getV4AllNodes(this.selectedTree);
    const sorted = [...allNodes].sort((a, b) => a.xpThreshold - b.xpThreshold);

    const leftX = 100;
    const rightX = 290;
    const halfLen = Math.ceil(sorted.length / 2);

    for (let n = 0; n < sorted.length; n++) {
      const col = n < halfLen ? 0 : 1;
      const row = col === 0 ? n : n - halfLen;
      const x = col === 0 ? leftX : rightX;
      const y = NODE_START_Y + row * NODE_ROW_HEIGHT;
      this.renderNode(sorted[n], x, y, player);
    }
  }

  /**
   * Renders a single node row at (x, y), coloring it by unlock/attune state and wiring
   * hover and click handlers for active/evolution nodes.
   */
  private renderNode(
    node: V4NodeDefinition | V4ApotheosisDefinition,
    x: number,
    y: number,
    player: { skillProgression: import('../components/SkillProgressionComponent').SkillProgressionComponent; attunement: import('../components/AttunementComponent').AttunementComponent },
  ): void {
    const isUnlocked = player.skillProgression.isUnlocked(node.id);
    const attuned = player.attunement.isAttuned(node.id);

    let color: string;
    let prefix = '';
    const isActiveType = node.nodeType === 'active' || node.nodeType === 'evolution';

    if (isUnlocked && attuned && isActiveType) {
      color = Hex.cyan;
      prefix = '\u25c6 '; // ◆
    } else if (isUnlocked && node.nodeType === 'passive') {
      color = '#66cc66';
      prefix = '[P] ';
    } else if (isUnlocked && node.nodeType === 'upgrade') {
      color = '#999999';
      prefix = '[U] ';
    } else if (isUnlocked && node.nodeType === 'evolution') {
      color = '#ddaa44';
      prefix = '[E] ';
    } else if (isUnlocked) {
      color = '#aaaaaa';
    } else {
      color = Hex.textDim;
      prefix = '\u2022 '; // •
    }

    const label = `${prefix}${node.name}`;
    const nodeText = crispText(this, x, y, label, {
      fontFamily: Font.family, fontSize: Font.sizeLabel, color,
    }).setOrigin(0, 0.5).setInteractive({ useHandCursor: isUnlocked && isActiveType });

    if (isUnlocked && isActiveType) {
      nodeText.on('pointerdown', () => this.onNodeClick(this.selectedTree, node));
    }
    nodeText.on('pointerover', () => {
      this.onNodeHover(node);
      if (isUnlocked && isActiveType) nodeText.setColor(Hex.textPrimary);
    });
    nodeText.on('pointerout', () => {
      nodeText.setColor(color);
    });

    this.nodeGroup.push(nodeText);
  }

  // ---------------------------------------------------------------------------
  // Info panel
  // ---------------------------------------------------------------------------

  /** Redraws the right-hand info panel with details for the hovered or selected skill. */
  private rebuildInfo(): void {
    for (const obj of this.infoGroup) obj.destroy();
    this.infoGroup = [];

    const infoX = GAME_WIDTH - 100;
    const infoTopY = NODE_START_Y;

    const headerText = crispText(this, infoX, infoTopY - 12, '— SKILL INFO —', TextStyle.bodyMuted).setOrigin(1, 0.5);
    this.infoGroup.push(headerText);

    const infoStyle = {
      fontFamily: Font.family, fontSize: Font.sizeLabel, color: '#999999',
      wordWrap: { width: 320 },
    } as Phaser.Types.GameObjects.Text.TextStyle;

    if (!this.selectedSkillId) {
      const hint = crispText(this, infoX, infoTopY,
        'Hover over a skill to see details', infoStyle,
      ).setOrigin(1, 0);
      this.infoGroup.push(hint);
      return;
    }

    const player = this.gameSceneRef.player;
    const def = ConfigLoader.getV4Node(this.selectedSkillId);
    if (!def) return;

    const treeName = ConfigLoader.getV4SkillTree(this.selectedSkillId) ?? '?';
    const treeXp = player.skillProgression.getTreeXp(treeName);
    const isUnlocked = player.skillProgression.isUnlocked(this.selectedSkillId);

    const typeLabel = def.nodeType === 'passive' ? 'Passive (always-on)'
      : def.nodeType === 'upgrade' ? 'Upgrade'
      : def.nodeType === 'evolution' ? 'Evolution'
      : 'Active Skill';

    const statusLabel = isUnlocked ? 'Unlocked' : `Locked (need ${def.xpThreshold} XP)`;

    const lines = [
      `${def.name} — ${typeLabel}`,
      def.description,
      `${statusLabel}  |  Tree XP: ${Math.floor(treeXp)}`,
    ];

    const infoText = crispText(this, infoX, infoTopY, lines.join('\n'), infoStyle).setOrigin(1, 0);
    this.infoGroup.push(infoText);
  }

  // ---------------------------------------------------------------------------
  // Slots remaining
  // ---------------------------------------------------------------------------

  /** Redraws the slot counter label below the hotbar, switching to an assignment prompt during pending flow. */
  private rebuildSlotsRemaining(): void {
    if (this.slotsRemainingText) { this.slotsRemainingText.destroy(); this.slotsRemainingText = null; }

    const player = this.gameSceneRef.player;
    const slotsStyle = { fontFamily: Font.family, fontSize: Font.sizeLabel, color: Hex.cyan } as Phaser.Types.GameObjects.Text.TextStyle;

    let counterText = `Slots: ${player.attunement.availableSlots} / ${player.attunement.totalSlots} available`;
    if (this.pendingAttuneId) {
      const def = ConfigLoader.getV4Node(this.pendingAttuneId);
      counterText = `Click a slot to assign ${def?.name ?? this.pendingAttuneId}`;
    }

    this.slotsRemainingText = crispText(this, GAME_WIDTH / 2, HOTBAR_Y + 24, counterText, slotsStyle).setOrigin(0.5);
  }

  // ---------------------------------------------------------------------------
  // Interaction handlers
  // ---------------------------------------------------------------------------

  /** Unattunes the skill in the given slot and deactivates it if it was toggled on. */
  private onSlotClick(slotIndex: number): void {
    const player = this.gameSceneRef.player;
    const skillId = player.attunement.getSkillAtSlot(slotIndex);
    if (skillId) {
      player.attunement.unattune(skillId);
      if (player.skillActivation.isSkillActive(skillId)) {
        player.skillActivation.tryToggle(skillId);
      }
      this.rebuildAll();
    }
  }

  /** Commits a pending skill to the chosen slot and clears the pending state. */
  private onSlotAssign(slotIndex: number, skillId: string): void {
    const player = this.gameSceneRef.player;
    player.attunement.attuneToSlot(skillId, slotIndex);
    this.pendingAttuneId = null;
    this.rebuildAll();
  }

  /**
   * Handles a node click: if the node is unlocked and active/evolution type, toggles attunement
   * or begins the pending-slot-assignment flow. Clicking a different node cancels any pending.
   */
  private onNodeClick(_treeName: string, node: V4NodeDefinition | V4ApotheosisDefinition): void {
    const player = this.gameSceneRef.player;

    if (this.pendingAttuneId && this.pendingAttuneId !== node.id) {
      this.pendingAttuneId = null;
    }

    const isUnlocked = player.skillProgression.isUnlocked(node.id);
    if (!isUnlocked) {
      this.selectedSkillId = node.id;
      this.rebuildAll();
      return;
    }

    if (node.nodeType === 'active' || node.nodeType === 'evolution') {
      if (player.attunement.isAttuned(node.id)) {
        player.attunement.unattune(node.id);
        if (player.skillActivation.isSkillActive(node.id)) {
          player.skillActivation.tryToggle(node.id);
        }
        this.rebuildAll();
      } else if (player.attunement.canAttune(node.id)) {
        this.pendingAttuneId = node.id;
        this.rebuildAll();
      }
    }
  }

  /** Updates the selected skill to the hovered node and refreshes the info panel. */
  private onNodeHover(node: V4NodeDefinition | V4ApotheosisDefinition): void {
    this.selectedSkillId = node.id;
    this.rebuildInfo();
  }
}
